import BASE_URL from '../config/api';
import { getStoredAuthToken, getStoredAuthUser } from './auth';

const MAX_CACHE_ENTRIES = 64;
const AUTHENTICATED_AVATAR_PATH = /^\/api\/auth\/teammates\/[^/?]+\/avatar$/;

let activeSessionKey = '';
let sessionGeneration = 0;
const cache = new Map();
const inFlight = new Map();
const keyVersions = new Map();
const retiredEntries = new Map();

const revoke = (entry) => {
  if (entry?.objectUrl) URL.revokeObjectURL(entry.objectUrl);
};

const retire = (entry, force = false) => {
  if (!entry) return;
  if (!force && entry.leases > 0) {
    entry.retired = true;
    retiredEntries.set(entry.src, entry);
    return;
  }
  revoke(entry);
  retiredEntries.delete(entry.src);
};

const clearEntries = () => {
  inFlight.forEach((entry) => entry.controller.abort());
  inFlight.clear();
  cache.forEach((entry) => retire(entry, true));
  retiredEntries.forEach((entry) => retire(entry, true));
  cache.clear();
  keyVersions.clear();
};

const sessionIdentity = ({ token, user }) => {
  const userId = String(user?.id || user?._id || '').trim();
  const tenantId = String(user?.clientAccountId || '').trim();
  return token && userId ? `${userId}:${tenantId}:${token}` : '';
};

const configuredBackendOrigin = () => {
  if (!BASE_URL) return window.location?.origin || '';
  try {
    return new URL(BASE_URL).origin;
  } catch {
    return '';
  }
};

const resolveAuthenticatedAvatarRequestUrl = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  try {
    const isAbsolute = /^https?:\/\//i.test(normalized);
    const base = BASE_URL || window.location?.origin || '';
    if (!base) return '';
    const parsed = new URL(normalized, `${base.replace(/\/+$/, '')}/`);
    if (!AUTHENTICATED_AVATAR_PATH.test(parsed.pathname)) return '';
    if (isAbsolute && parsed.origin !== configuredBackendOrigin()) return '';
    return parsed.toString();
  } catch {
    return '';
  }
};

export const isAuthenticatedPersonalAvatarUrl = (value) => Boolean(
  resolveAuthenticatedAvatarRequestUrl(value)
);

export const configurePersonalAvatarSession = ({ token, user }) => {
  const nextKey = sessionIdentity({ token, user });
  if (nextKey === activeSessionKey) return;
  sessionGeneration += 1;
  activeSessionKey = nextKey;
  clearEntries();
};

export const clearPersonalAvatarCache = () => {
  sessionGeneration += 1;
  activeSessionKey = '';
  clearEntries();
};

const ensureCurrentSession = (token, user) => {
  configurePersonalAvatarSession({ token, user });
  return activeSessionKey;
};

const touch = (key, entry) => {
  cache.delete(key);
  cache.set(key, entry);
};

const enforceBound = () => {
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    const oldest = cache.get(oldestKey);
    cache.delete(oldestKey);
    retire(oldest);
  }
};

const invalidateMatching = (predicate) => {
  [...cache.keys()].filter(predicate).forEach((key) => {
    retire(cache.get(key));
    cache.delete(key);
    keyVersions.set(key, (keyVersions.get(key) || 0) + 1);
  });
  [...inFlight.keys()].filter(predicate).forEach((key) => {
    inFlight.get(key)?.controller.abort();
    inFlight.delete(key);
    keyVersions.set(key, (keyVersions.get(key) || 0) + 1);
  });
};

export const invalidatePersonalAvatarForUser = (userId) => {
  const encoded = encodeURIComponent(String(userId || '').trim());
  if (!encoded) return;
  invalidateMatching((key) => key.includes(`/api/auth/teammates/${encoded}/avatar`));
};

export const retainPersonalAvatarSource = (source) => {
  const entry = [...cache.values()].find((candidate) => candidate.src === source);
  if (entry) entry.leases += 1;
};

export const releasePersonalAvatarSource = (source) => {
  const entry = [...cache.values()].find((candidate) => candidate.src === source)
    || retiredEntries.get(source);
  if (!entry || entry.leases === 0) return;
  entry.leases -= 1;
  if (entry.leases === 0 && entry.retired) revoke(entry);
  if (entry.leases === 0 && entry.retired) retiredEntries.delete(source);
};

export const loadPersonalAvatar = async (avatarUrl, options = {}) => {
  const normalizedUrl = String(avatarUrl || '').trim();
  const requestUrl = resolveAuthenticatedAvatarRequestUrl(normalizedUrl);
  if (!requestUrl) return normalizedUrl;

  const token = options.token || getStoredAuthToken();
  const user = options.user || getStoredAuthUser();
  const currentSession = ensureCurrentSession(token, user);
  if (!currentSession || !token) return '';

  const key = `${currentSession}|${normalizedUrl}`;
  const cached = cache.get(key);
  if (cached) {
    touch(key, cached);
    return cached.src;
  }
  if (inFlight.has(key)) return inFlight.get(key).promise;

  const controller = new AbortController();
  const capturedGeneration = sessionGeneration;
  const capturedVersion = keyVersions.get(key) || 0;
  const promise = (async () => {
    const response = await fetch(requestUrl, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (response.status === 404) return '';
    if (!response.ok) throw new Error(`PERSONAL_AVATAR_HTTP_${response.status}`);

    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    if (!/^image\/(?:png|jpeg|webp|gif)(?:;|$)/.test(contentType)) {
      throw new Error('PERSONAL_AVATAR_CONTENT_TYPE_INVALID');
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const isCurrent = (
      capturedGeneration === sessionGeneration
      && currentSession === activeSessionKey
      && capturedVersion === (keyVersions.get(key) || 0)
    );
    if (!isCurrent) {
      URL.revokeObjectURL(objectUrl);
      return '';
    }

    const entry = { src: objectUrl, objectUrl, leases: 0, retired: false };
    cache.set(key, entry);
    enforceBound();
    return objectUrl;
  })();
  inFlight.set(key, { controller, promise });

  try {
    return await promise;
  } finally {
    if (inFlight.get(key)?.promise === promise) inFlight.delete(key);
  }
};

export const getPersonalAvatarCacheStateForTests = () => ({
  activeSessionKey,
  cacheSize: cache.size,
  inFlightSize: inFlight.size,
  maxEntries: MAX_CACHE_ENTRIES,
  generation: sessionGeneration,
  retiredSize: retiredEntries.size,
});
